import { Farm, MpxResponseBaseSingle, UserId } from '@cibo/core'
import { FriendlyError, ResponsiveDialog, SubmitButton } from '@cibo/ui'
import AddIcon from '@mui/icons-material/Add'
import { Box, Button, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material'
import { Form, Formik, useFormikContext } from 'formik'
import { useSnackbar } from 'notistack'
import { pluck, uniq } from 'ramda'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'
import { useLandManagement } from '../../../../../'
import { useAssignFieldsToFarm, useUnassignFieldsToFarm } from '../../../../../queries/useFarms'
import { useField, useFields } from '../../../../../queries/useFields'
import { AssignFarmField } from './AssignFarmField'

type FarmFormModalProps = {
  farmerId?: UserId
  open: boolean
  onCancel(): void
  onSuccess(result: any): void
  resourceIds?: string[]
}

const AssignFarmDialog = ({
  open,
  farmerId,
  onCancel,
  onSuccess,
  resourceIds,
}: FarmFormModalProps) => {
  const { t } = useTranslation('@cibo/landmanager/AssignFarmDialog')
  const { resetForm, setFieldValue } = useFormikContext()
  const { openFarmDialog } = useLandManagement()

  const fields = useFields(resourceIds)
  const ownerIds = fields.data && uniq(pluck('owner', fields.data))

  const disallowed = Boolean(
    resourceIds && Object.values(fields.data || {}).find(model => !model.can(['farm_assign']))
  )

  // This is prevented from LM table but to support other use cases
  const multipleOwners = ownerIds && ownerIds.length > 1

  useEffect(() => {
    if (!open) {
      resetForm()
    }
  }, [open])

  return (
    <ResponsiveDialog open={open} onClose={onCancel}>
      <DialogTitle>{t('assignFarm', { count: resourceIds?.length })}</DialogTitle>

      <DialogContent>
        <Stack alignItems="flex-end">
          {multipleOwners && <FriendlyError message={t('multipleOwners')} />}

          <AssignFarmField
            name="farm"
            farmerId={farmerId}
            data-testid="select-farm"
            disabled={disallowed}
          />

          <Box>
            <Button
              startIcon={<AddIcon />}
              onClick={() =>
                openFarmDialog({
                  onSuccess: (result: MpxResponseBaseSingle<Farm>) => {
                    setFieldValue('farm', result.item)
                  },
                  ownerId: ownerIds && ownerIds[0].userId,
                })
              }
              data-testid="add-new-farm"
            >
              {t('addNewFarm')}
            </Button>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onCancel} data-testid="cancel-assign-farm">
          {t('cancel')}
        </Button>
        <SubmitButton data-testid="submit-assign-farm" disabled={multipleOwners || disallowed}>
          {t('assign')}
        </SubmitButton>
      </DialogActions>
    </ResponsiveDialog>
  )
}

export const AssignFarmModal = (props: FarmFormModalProps) => {
  const { t } = useTranslation('@cibo/landmanager/AssignFarmDialog')
  const { enqueueSnackbar } = useSnackbar()
  const field = useField(props.resourceIds && props.resourceIds[0])

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        farm: yup.object().required('required').nullable(),
      }),
    []
  )
  const assignFields = useAssignFieldsToFarm()
  const unassignFields = useUnassignFieldsToFarm()

  const handleSubmitForm = (values: any) => {
    if (!props.resourceIds) return

    const request = {
      termsetId: values.farm.id,
      fieldIds: props.resourceIds,
    }

    if (values.farm.id === 'none') {
      if (!field.data?.farm?.id) return props.onSuccess && props.onSuccess(values)

      const request = {
        termsetId: field.data.farm.id,
        fieldIds: props.resourceIds,
      }

      return unassignFields.mutateAsync(request).then((result: any) => {
        enqueueSnackbar(t('success', request), {
          variant: 'success',
        })

        props.onSuccess && props.onSuccess(result)
      })
    }

    return assignFields
      .mutateAsync(request)
      .then((result: any) => {
        enqueueSnackbar(t('success', request), {
          variant: 'success',
        })

        props.onSuccess && props.onSuccess(result)
      })
      .catch(error => {
        enqueueSnackbar(`${t('errorOnAssign')}`, {
          variant: 'error',
        })

        throw error
      })
  }

  const initialValues = {
    farm: field.data?.farm ? { id: field.data?.farm.id, name: field.data?.farm.item.name } : null,
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmitForm}
      enableReinitialize
    >
      <Form>
        <AssignFarmDialog {...props} farmerId={field.data?.owner?.userId} />
      </Form>
    </Formik>
  )
}
